import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useState } from "react";
import { Logo } from "@/components/layout/Logo";
import { LangToggle } from "@/components/layout/LangToggle";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";

const linkCls = ({ isActive }) =>
  `text-sm font-medium px-1 py-1 border-b-2 ${isActive ? "border-cyan text-navy" : "border-transparent text-navy/70 hover:text-navy"}`;

const UserActions = ({ onDone }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  if (user === null) return <div className="h-9 w-24" />;
  if (!user)
    return (
      <div className="flex items-center gap-2">
        <Link to="/login" onClick={onDone} className="btn-ghost" data-testid="nav-login-link">{t("nav.login")}</Link>
        <Link to="/signup" onClick={onDone} className="btn-primary !py-2.5" data-testid="nav-signup-link">{t("nav.signup")}</Link>
      </div>
    );
  return (
    <div className="flex flex-wrap items-center gap-2">
      {user.role === "admin" && (
        <Link to="/admin" onClick={onDone} className="btn-ghost" data-testid="nav-admin-link"><Shield className="h-4 w-4" />{t("nav.admin")}</Link>
      )}
      <Link to="/dashboard" onClick={onDone} className="btn-ghost" data-testid="nav-dashboard-link">
        {user.picture ? <img src={user.picture} alt="" className="h-5 w-5 rounded-full" /> : <LayoutDashboard className="h-4 w-4" />}
        {t("nav.dashboard")}
      </Link>
      <button
        data-testid="nav-logout-button"
        onClick={async () => { await logout(); onDone?.(); navigate("/"); }}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-navy/70 hover:bg-navy/5 hover:text-navy"
        aria-label={t("nav.logout")} title={t("nav.logout")}
      >
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-white/40 bg-white/80 backdrop-blur-xl" data-testid="site-header">
      <nav className="container-z flex h-16 items-center justify-between gap-4" aria-label="Main">
        <Logo />
        <div className="hidden items-center gap-7 md:flex">
          <NavLink to="/solve" className={linkCls} data-testid="nav-solve-link">{t("nav.solve")}</NavLink>
          <NavLink to="/docs" className={linkCls} data-testid="nav-docs-link">{t("nav.docs")}</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about-link">{t("nav.about")}</NavLink>
        </div>
        <div className="hidden items-center gap-3 md:flex">
          <LangToggle />
          <UserActions />
        </div>
        <div className="flex items-center gap-2 md:hidden">
          <LangToggle />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-navy text-white" aria-label={t("nav.menu")} data-testid="nav-mobile-menu-button">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm bg-white">
              <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
              <div className="mt-8 flex flex-col gap-5">
                {[["/solve", "nav.solve"], ["/docs", "nav.docs"], ["/about", "nav.about"], ["/contact", "nav.contact"]].map(([to, k]) => (
                  <Link key={to} to={to} onClick={() => setOpen(false)} className="text-lg font-semibold text-navy hover:text-brand" data-testid={`mobile-nav-${k.split(".")[1]}-link`}>{t(k)}</Link>
                ))}
                <div className="border-t border-navy/10 pt-5"><UserActions onDone={() => setOpen(false)} /></div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
